import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useQuery, useLazyQuery, NetworkStatus } from '@apollo/client';
import { useSelector } from 'react-redux';
import { LIST_USAGES_BY_COMPANY, GET_EVALUATION, GET_SESSION, GET_SCENARIO, LIST_CONVERSATIONS, LIST_EVALUATIONS_BY_USER } from '../../graphql/queries';
import { RootState } from '../../store';
import { canAccess } from '../../utils/permissions';
import { generateMarkdownReport } from '../../utils/formatUtils';
import { uploadService } from '../../services/uploadService';
import CsvExportButton from './components/CsvExportButton';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  useTheme,
  CircularProgress,
  Collapse,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';

interface Usage {
  usageId?: string;
  companyId?: string;
  userId?: string;
  userDisplayName?: string;
  department?: string;
  userDepartments?: string[];
  scenarioId?: string;
  scenarioTitle?: string;
  lastEvaluateDate?: string;
  usageCount?: number;
  lastEvaluationId?: string;
  lastEvaluationSummary?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface EvaluationHistory {
  evaluationId: string;
  sessionId: string;
  userId: string;
  scenarioId: string;
  companyId: string;
  department?: string;
  userDepartments?: string[];
  results: string;
  resultsSummary?: string;
  resultPdfPath?: string;
  createdAt: string;
  updatedAt: string;
}


const AnalyticsPage: React.FC = () => {
  const theme = useTheme();
  // Reduxからユーザー情報を取得
  const { currentUser } = useSelector((state: RootState) => state.user);
  const companyId = currentUser?.companyId || '';
  const permCsvDownload = canAccess(currentUser, 'analytics.view.canCsvDownload');
  
  // ユーザーIDから企業内ユーザーIDを抽出する関数
  const extractInternalUserId = (userId: string | undefined): string => {
    if (!userId) return '不明';
    return userId.indexOf('#') !== -1 ? userId.substring(userId.indexOf('#') + 1) : userId;
  };

  // 表示件数
  const DISPLAY_INCREMENT = 1000;
  /** この件数以上を一度にテーブル描画するときはオーバーレイを先に出す（検索解除・大量ヒット時のフリーズ対策） */
  const HEAVY_LOCAL_FILTER_ROWS = 2000;

  // 状態管理
  const [usages, setUsages] = useState<Usage[]>([]);
  const [filteredUsages, setFilteredUsages] = useState<Usage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchKeywords, setSearchKeywords] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [expandedUsageId, setExpandedUsageId] = useState<string | null>(null);
  const [evaluationHistories, setEvaluationHistories] = useState<{ [key: string]: EvaluationHistory[] }>({});
  const [loadingHistories, setLoadingHistories] = useState<{ [key: string]: boolean }>({});

  // オーバーレイ用状態
  const [postFetchPaintHold, setPostFetchPaintHold] = useState(false);
  const [clientFilterPaintHold, setClientFilterPaintHold] = useState(false);
  /** 検索の「Clear」後に refetch 完了まで、古いキャッシュの listUsages を usages に流し込まない */
  const [listHydrationPaused, setListHydrationPaused] = useState(false);

  // Refs
  const initialDataLoadedRef = useRef<boolean>(false);
  const prevListFetchBusyRef = useRef(false);
  const filterEffectGenRef = useRef(0);
  const prevUsagesLengthRef = useRef<number>(0);
  const lastAutoLoadTokenRef = useRef<string | null>(null);
  const isFirstAutoLoadCheckRef = useRef<boolean>(true);

  // 評価一覧を取得
  const { data: usagesData, loading: usagesLoading, networkStatus, error: usagesError, fetchMore, refetch: refetchUsages } = useQuery(
    LIST_USAGES_BY_COMPANY,
    {
      variables: { companyId, limit: DISPLAY_INCREMENT },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: !companyId
    }
  );

  // セッション情報を取得するためのLazyクエリ
  const [getSession] = useLazyQuery(GET_SESSION);
  
  // シナリオ情報を取得するためのLazyクエリ
  const [getScenario] = useLazyQuery(GET_SCENARIO);

  // 評価情報を取得するためのLazyクエリ
  const [getEvaluation] = useLazyQuery(GET_EVALUATION);

  // 会話履歴を取得するためのLazyクエリ
  const [getConversations] = useLazyQuery(LIST_CONVERSATIONS);

  // 評価履歴を取得するためのLazyクエリ（該当ユーザーのみ取得してシナリオでフィルタ）
  const [getEvaluationsByUser] = useLazyQuery(LIST_EVALUATIONS_BY_USER);

  // 一覧取得中フラグ
  const listFetchBusy =
    isAutoLoading ||
    usagesLoading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.refetch;

  // 一覧APIの取得が終わってから、大量行の描画がコミットされるまでオーバーレイを維持
  const showUsageListOverlay =
    Boolean(companyId) &&
    (listFetchBusy || postFetchPaintHold || clientFilterPaintHold);

  useLayoutEffect(() => {
    if (listFetchBusy) {
      setPostFetchPaintHold(false);
      setClientFilterPaintHold(false);
    } else if (prevListFetchBusyRef.current) {
      setPostFetchPaintHold(true);
    }
    prevListFetchBusyRef.current = listFetchBusy;
  }, [listFetchBusy]);

  useLayoutEffect(() => {
    if (!postFetchPaintHold) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setPostFetchPaintHold(false);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [postFetchPaintHold, usages, filteredUsages]);

  // 評価データが取得できたら状態を更新
  useEffect(() => {
    if (listHydrationPaused) return;
    if (usagesData?.listUsagesByCompany?.items) {
      setUsages(usagesData.listUsagesByCompany.items);
      setHasMore(!!usagesData.listUsagesByCompany.nextToken);

      if (!initialDataLoadedRef.current) {
        initialDataLoadedRef.current = true;
        requestAnimationFrame(() => {
          setIsInitialLoad(false);
        });
      }
    }
  }, [usagesData, listHydrationPaused]);

  // 検索キーワードが変更されたときにフィルタリング（クライアントのみ。DBは listUsages のページングで取得）
  useEffect(() => {
    const nextFiltered =
      searchKeywords.length === 0
        ? usages
        : usages.filter(usage => {
            return searchKeywords.some(keyword => {
              const term = keyword.trim().toLowerCase();
              if (!term) return false;
              return (
                (usage.scenarioTitle && usage.scenarioTitle.toLowerCase().includes(term)) ||
                (usage.userDisplayName && usage.userDisplayName.toLowerCase().includes(term)) ||
                (usage.userId && extractInternalUserId(usage.userId).toLowerCase().includes(term)) ||
                (usage.userDepartments && usage.userDepartments.some(d => d.toLowerCase().includes(term)))
              );
            });
          });

    const gen = ++filterEffectGenRef.current;
    const isCurrentGen = () => filterEffectGenRef.current === gen;

    if (nextFiltered.length < HEAVY_LOCAL_FILTER_ROWS) {
      setClientFilterPaintHold(false);
      setFilteredUsages(nextFiltered);
      return;
    }

    setClientFilterPaintHold(true);
    let raf1 = 0;
    let raf2 = 0;
    let raf3 = 0;
    raf1 = requestAnimationFrame(() => {
      if (!isCurrentGen()) return;
      setFilteredUsages(nextFiltered);
      raf2 = requestAnimationFrame(() => {
        if (!isCurrentGen()) return;
        raf3 = requestAnimationFrame(() => {
          if (isCurrentGen()) {
            setClientFilterPaintHold(false);
          }
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cancelAnimationFrame(raf3);
      if (filterEffectGenRef.current === gen) {
        setClientFilterPaintHold(false);
      }
    };
  }, [searchKeywords, usages]);

  // フィルタリング結果がDISPLAY_INCREMENT未満の場合、自動的に追加取得
  useEffect(() => {
    if (listFetchBusy || postFetchPaintHold || clientFilterPaintHold || !hasMore) {
      prevUsagesLengthRef.current = usages.length;
      return;
    }

    if (isInitialLoad) {
      prevUsagesLengthRef.current = usages.length;
      return;
    }

    if (isFirstAutoLoadCheckRef.current) {
      isFirstAutoLoadCheckRef.current = false;
      prevUsagesLengthRef.current = usages.length;
      return;
    }

    const currentToken = usagesData?.listUsagesByCompany?.nextToken || null;
    const hasNewToken = currentToken && currentToken !== lastAutoLoadTokenRef.current;
    const hasNewUsages = usages.length !== prevUsagesLengthRef.current;

    const shouldAutoLoad =
      filteredUsages.length < DISPLAY_INCREMENT &&
      currentToken &&
      (hasNewUsages || hasNewToken);

    if (shouldAutoLoad) {
      prevUsagesLengthRef.current = usages.length;
      lastAutoLoadTokenRef.current = currentToken;

      const autoLoadMore = async () => {
        setIsAutoLoading(true);
        try {
          await fetchMore({
            variables: {
              companyId,
              limit: DISPLAY_INCREMENT,
              nextToken: currentToken,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prev;
              const prevItems = prev.listUsagesByCompany.items;
              const newItems = fetchMoreResult.listUsagesByCompany.items;
              const existingIds = new Set(prevItems.map((u: Usage) => u.usageId));
              const uniqueNewItems = newItems.filter((u: Usage) => !existingIds.has(u.usageId));
              const updatedItems = [...prevItems, ...uniqueNewItems];
              return {
                listUsagesByCompany: {
                  __typename: prev.listUsagesByCompany.__typename,
                  items: updatedItems,
                  nextToken: fetchMoreResult.listUsagesByCompany.nextToken
                }
              };
            }
          });
        } catch (error) {
          console.error('利用データの自動取得に失敗しました:', error);
        } finally {
          setIsAutoLoading(false);
        }
      };

      autoLoadMore();
    } else {
      prevUsagesLengthRef.current = usages.length;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsages.length, usages.length, hasMore, listFetchBusy, postFetchPaintHold, clientFilterPaintHold, companyId, usagesData?.listUsagesByCompany?.nextToken]);

  // さらに取得ボタンのクリックハンドラー
  const handleLoadMore = async () => {
    if (usagesData?.listUsagesByCompany?.nextToken) {
      try {
        await fetchMore({
          variables: {
            companyId,
            limit: DISPLAY_INCREMENT,
            nextToken: usagesData.listUsagesByCompany.nextToken
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;
            const prevItems = prev.listUsagesByCompany.items;
            const newItems = fetchMoreResult.listUsagesByCompany.items;
            const existingIds = new Set(prevItems.map((u: Usage) => u.usageId));
            const uniqueNewItems = newItems.filter((u: Usage) => !existingIds.has(u.usageId));
            const updatedItems = [...prevItems, ...uniqueNewItems];
            return {
              listUsagesByCompany: {
                __typename: prev.listUsagesByCompany.__typename,
                items: updatedItems,
                nextToken: fetchMoreResult.listUsagesByCompany.nextToken
              }
            };
          }
        });
      } catch (error) {
        console.error('利用データの取得に失敗しました:', error);
      }
    } else {
      setHasMore(false);
    }
  };

  // 日付フォーマット関数
  const formatDate = (dateString: string) => {
    if (!dateString) return '不明';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 評価内容をダウンロードする関数
  const handleDownloadEvaluation = async (usage: Usage) => {
    if (!usage.lastEvaluationId) {
      alert('評価内容が見つかりません。');
      return;
    }
    
    try {
      const evaluationResult = await getEvaluation({
        variables: { evaluationId: usage.lastEvaluationId }
      });
      const evaluation = evaluationResult.data.getEvaluation;
      if (!evaluation) {
        alert('評価情報が見つかりません。');
        return;
      }

      // PDFがあればそれを表示、PDFがなければMarkdownをそのまま取得
      if(evaluation.resultPdfPath) {
        try {
          const { presignedUrl } = await uploadService.getEvaluationUrl(usage.lastEvaluationId);
          window.open(presignedUrl, '_blank');
        } catch (error: any) {
          console.error('PDFを開く際にエラーが発生しました:', error);
        }
      } else {
        // シナリオ情報を取得
        const scenarioResult = await getScenario({
          variables: { scenarioId: usage.scenarioId }
        });
        const scenario = scenarioResult.data.getScenario;
        if (!scenario) {
          alert('シナリオ情報が見つかりません。');
          return;
        }

        // セッション情報を取得
        const sessionResult = await getSession({
          variables: { sessionId: evaluation.sessionId }
        });
        const session = sessionResult.data.getSession;
        if (!session) {
          alert('セッション情報が見つかりません。');
          return;
        }

        // 会話履歴を取得
        const conversationsResult = await getConversations({
          variables: { sessionId: evaluation.sessionId, limit: 1000 }
        });
        const conversations = conversationsResult.data?.listConversations?.items || [];
        
        // 評価情報オブジェクトを作成
        const evaluationInfo = {
          session,
          evaluationText: evaluation.results,
          conversations,
          date: formatDate(evaluation.createdAt),
        };
        
        // Markdownレポートを生成
        const markdown = generateMarkdownReport(scenario, evaluationInfo, usage.userDisplayName);
        
        if (!markdown) {
          alert('レポートを生成できませんでした。');
          return;
        }
        
        // Blobオブジェクトを作成
        const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
        
        // ダウンロードリンクを作成
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // ファイル名を設定
        const fileName = `評価レポート_${scenario.title || 'シナリオ'}_${new Date().toISOString().split('T')[0]}.txt`;
        link.download = fileName;
        
        // リンクをクリックしてダウンロード
        document.body.appendChild(link);
        link.click();
        
        // クリーンアップ
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('評価内容のダウンロード中にエラーが発生しました:', error);
      alert('評価内容のダウンロードに失敗しました。');
    }
  };


  // 行クリックハンドラー（アコーディオンの展開/折りたたみ）
  const handleRowClick = async (usage: Usage) => {
    const usageId = usage.usageId;
    if (!usageId) return;

    // 既に展開されている場合は折りたたむ
    if (expandedUsageId === usageId) {
      setExpandedUsageId(null);
      return;
    }

    // 展開する
    setExpandedUsageId(usageId);

    // 既に取得済みの場合は再取得しない
    if (evaluationHistories[usageId]) {
      return;
    }

    // 該当ユーザー・シナリオの評価履歴を全件取得（listEvaluationsByUser でユーザー単位取得し、シナリオでフィルタ）
    setLoadingHistories(prev => ({ ...prev, [usageId]: true }));
    try {
      const PAGE_LIMIT = 200;
      let allItems: EvaluationHistory[] = [];
      let nextToken: string | null = null;
      let result: Awaited<ReturnType<typeof getEvaluationsByUser>>;

      do {
        result = await getEvaluationsByUser({
          variables: {
            userId: usage.userId,
            limit: PAGE_LIMIT,
            ...(nextToken && { nextToken })
          }
        });

        const items = result.data?.listEvaluationsByUser?.items ?? [];
        allItems = allItems.concat(items as EvaluationHistory[]);
        nextToken = result.data?.listEvaluationsByUser?.nextToken ?? null;
      } while (nextToken);

      // この usage の scenarioId に一致する評価のみに絞る（userId は既にクエリで一致）
      const filteredEvaluations = allItems.filter(
        (evaluation: EvaluationHistory) => evaluation.scenarioId === usage.scenarioId
      );

      // 作成日時の降順でソート
      const sortedEvaluations = [...filteredEvaluations].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setEvaluationHistories(prev => ({
        ...prev,
        [usageId]: sortedEvaluations
      }));
    } catch (error) {
      console.error('評価履歴の取得中にエラーが発生しました:', error);
    } finally {
      setLoadingHistories(prev => ({ ...prev, [usageId]: false }));
    }
  };

  // 個別の評価をダウンロードする関数
  const handleDownloadIndividualEvaluation = async (evaluation: EvaluationHistory, usage: Usage) => {
    try {
      // PDFがあればそれを表示、PDFがなければMarkdownをそのまま取得
      if(evaluation.resultPdfPath) {
        try {
          const { presignedUrl } = await uploadService.getEvaluationUrl(evaluation.evaluationId);
          window.open(presignedUrl, '_blank');
        } catch (error: any) {
          console.error('PDFを開く際にエラーが発生しました:', error);
        }
      } else {
        // シナリオ情報を取得
        const scenarioResult = await getScenario({
          variables: { scenarioId: evaluation.scenarioId }
        });
        const scenario = scenarioResult.data.getScenario;
        if (!scenario) {
          alert('シナリオ情報が見つかりません。');
          return;
        }

        // セッション情報を取得
        const sessionResult = await getSession({
          variables: { sessionId: evaluation.sessionId }
        });
        const session = sessionResult.data.getSession;
        if (!session) {
          alert('セッション情報が見つかりません。');
          return;
        }

        // 会話履歴を取得
        const conversationsResult = await getConversations({
          variables: { sessionId: evaluation.sessionId, limit: 1000 }
        });
        const conversations = conversationsResult.data?.listConversations?.items || [];
        
        // 評価情報オブジェクトを作成
        const evaluationInfo = {
          session,
          evaluationText: evaluation.results,
          conversations,
          date: formatDate(session.startTime || evaluation.createdAt),
        };
        
        // Markdownレポートを生成
        const markdown = generateMarkdownReport(scenario, evaluationInfo, usage.userDisplayName);
        
        if (!markdown) {
          alert('レポートを生成できませんでした。');
          return;
        }
        
        // Blobオブジェクトを作成
        const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
        
        // ダウンロードリンクを作成
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // ファイル名を設定
        const fileName = `評価レポート_${scenario.title || 'シナリオ'}_${new Date(evaluation.createdAt).toISOString().split('T')[0]}.txt`;
        link.download = fileName;
        
        // リンクをクリックしてダウンロード
        document.body.appendChild(link);
        link.click();
        
        // クリーンアップ
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('評価内容のダウンロード中にエラーが発生しました:', error);
      alert('評価内容のダウンロードに失敗しました。');
    }
  };


  return (
    <Box sx={{ position: 'relative' }}>
      {showUsageListOverlay && (
        <Box
          role="status"
          aria-live="polite"
          aria-busy="true"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: (t) => t.zIndex.modal - 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            bgcolor: 'rgba(255, 255, 255, 0.88)',
            backdropFilter: 'blur(2px)',
            pointerEvents: 'all',
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            {listFetchBusy
              ? '評価データを取得しています…'
              : clientFilterPaintHold
                ? '検索・絞り込み結果を一覧に反映しています…'
                : '一覧を表示しています…'}
          </Typography>
        </Box>
      )}
      {/* 検索エリア */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        my: 2,
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CsvExportButton
            companyId={companyId}
            requestType="Summary"
            buttonLabel="サマリCSVダウンロード"
            searchKeywords={searchKeywords}
            disabled={!permCsvDownload}
            showHistory={false}
          />
          <CsvExportButton
            companyId={companyId}
            requestType="Evaluation"
            buttonLabel="評価明細CSVダウンロード"
            searchKeywords={searchKeywords}
            disabled={!permCsvDownload}
          />
        </Box>
        <Autocomplete
          multiple
          freeSolo
          clearText="検索クリア"
          clearIcon={<DeleteForeverIcon fontSize="small" />}
          options={[]}
          value={searchKeywords}
          inputValue={inputValue}
          onChange={(event, newValue, reason) => {
            // 右端の Clear のみ「ページ初期化」：メモリ上の大量リストを一度捨てて先頭ページから取り直す
            if (reason === 'clear') {
              filterEffectGenRef.current += 1;
              setClientFilterPaintHold(false);
              setListHydrationPaused(true);
              setSearchKeywords([]);
              setInputValue('');
              setUsages([]);
              setFilteredUsages([]);
              setHasMore(true);
              lastAutoLoadTokenRef.current = null;
              prevUsagesLengthRef.current = 0;
              isFirstAutoLoadCheckRef.current = true;
              if (companyId) {
                void refetchUsages({ companyId, limit: DISPLAY_INCREMENT }).finally(() => {
                  setListHydrationPaused(false);
                });
              } else {
                setListHydrationPaused(false);
              }
              return;
            }
            setSearchKeywords(newValue);
          }}
          onInputChange={(event, newInputValue, reason) => {
            setInputValue(newInputValue);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  {...tagProps}
                  sx={{
                    bgcolor: '#E0E0E0',
                    color: '#424242',
                    '& .MuiChip-deleteIcon': {
                      color: '#616161',
                      '&:hover': {
                        color: '#212121'
                      }
                    }
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={searchKeywords.length === 0 ? "検索項目：ロープレ名、氏名、ログインID、所属 OR検索" : ""}
              variant="outlined"
              size="small"
              onKeyDown={(e) => {
                // スペースキーまたはEnterキーで入力を確定
                if ((e.key === ' ' || e.key === 'Enter') && inputValue.trim()) {
                  e.preventDefault();
                  const trimmedValue = inputValue.trim();
                  if (trimmedValue && !searchKeywords.includes(trimmedValue)) {
                    setSearchKeywords([...searchKeywords, trimmedValue]);
                    setInputValue('');
                  }
                }
              }}
              inputProps={{ ...params.inputProps, maxLength: 600 }}
              sx={{
                width: '600px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '3px',
                  bgcolor: '#F9F9F9',
                  '& fieldset': {
                    borderColor: '#898E93',
                  }
                }
              }}
            />
          )}
        />
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        {usagesError ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" color="error">
              評価データの取得中にエラーが発生しました
            </Typography>
            <Typography variant="body2">{usagesError.message}</Typography>
          </Box>
        ) : (
        <TableContainer>
          <Table stickyHeader aria-label="評価一覧">
            <TableHead sx={{ bgcolor: '#F0F7FF' }}>
              <TableRow>
                <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '13px' }}>ロープレ名</TableCell>
                <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '13px' }}>氏名</TableCell>
                <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '13px' }}>ログインID</TableCell>
                <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '13px' }}>所属</TableCell>
                <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '13px' }}>回数</TableCell>
                <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '13px' }}>最終実施日</TableCell>
                <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '13px' }}>最新FB</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsages.map((usage, index) => (
                <React.Fragment key={usage.usageId}>
                  <TableRow 
                    hover 
                    onClick={() => handleRowClick(usage)}
                    sx={{ 
                      bgcolor: index % 2 === 0 ? '#FFFFFF' : '#F9F9F9',
                      '&:hover': { bgcolor: '#F5F5F5' },
                      cursor: 'pointer'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" sx={{ mr: 1 }}>
                          {expandedUsageId === usage.usageId ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                        {usage.scenarioTitle || '不明'}
                      </Box>
                    </TableCell>
                    <TableCell>{usage.userDisplayName || '不明'}</TableCell>
                    <TableCell>{extractInternalUserId(usage.userId)}</TableCell>
                    <TableCell>{(usage.userDepartments && usage.userDepartments.length > 0) ? usage.userDepartments.join(', ') : ''}</TableCell>
                    <TableCell>{usage.usageCount || 0}</TableCell>
                    <TableCell>{formatDate(usage.lastEvaluateDate || usage.updatedAt || '')}</TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation(); // 行クリックイベントを防ぐ
                          handleDownloadEvaluation(usage);
                        }}
                        title="最新評価をダウンロード"
                      >
                        <PdfIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                      <Collapse in={expandedUsageId === usage.usageId} timeout="auto" unmountOnExit>
                        <Box sx={{ ml: 6, mr: 2, my: 1 }}>
                          {loadingHistories[usage.usageId || ''] ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : evaluationHistories[usage.usageId || '']?.length > 0 ? (
                            <Box sx={{ maxWidth: '100%' }}>
                              <Table size="small" aria-label="評価履歴">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#F0F7FF' }}>
                                    <TableCell sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '12px', width: '200px' }}>評価日時</TableCell>
                                    <TableCell sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '12px', minWidth: '200px' }}>AI評価要約</TableCell>
                                    <TableCell align="center" sx={{ color: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', fontSize: '12px', width: '100px' }}>FB</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {evaluationHistories[usage.usageId || ''].map((evaluation, evalIndex) => (
                                    <TableRow 
                                      key={evaluation.evaluationId}
                                      sx={{ 
                                        bgcolor: evalIndex % 2 === 0 ? '#FAFAFA' : '#FFFFFF',
                                      }}
                                    >
                                      <TableCell sx={{ fontSize: '13px', py: 0.5 }}>{formatDate(evaluation.createdAt)}</TableCell>
                                      <TableCell sx={{ fontSize: '13px', py: 0.5, maxWidth: 400 }}>{evaluation.resultsSummary ?? '—'}</TableCell>
                                      <TableCell align="center" sx={{ py: 0.5 }}>
                                        <IconButton 
                                          size="small" 
                                          color="primary"
                                          onClick={() => handleDownloadIndividualEvaluation(evaluation, usage)}
                                          title="この評価をダウンロード"
                                        >
                                          <PdfIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ p: 1, color: 'text.secondary' }}>
                              履歴データがありません
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>
      
      {/* さらに表示するボタン */}
      {hasMore && (filteredUsages.length > 0 || isAutoLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleLoadMore}
            disabled={listFetchBusy}
            sx={{
              bgcolor: '#6C9AD7',
              color: 'white',
              fontSize: '16px',
              '&:hover': {
                bgcolor: '#6C9AD7',
                opacity: 0.9
              }
            }}
          >
            {listFetchBusy ? '読み込み中...' : 'さらに表示する'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AnalyticsPage;
